import { type ReactNode, useEffect, useState } from 'react';
import { Markdown } from '@plataforma/markdown';
import { AvisoCookies } from '@plataforma/markdown/cookies';
import { useApi } from './api';
import { useSesion } from './sesion';

interface Pendiente {
  id: string;
  tipo: string;
  version: number;
  titulo: string;
}

/**
 * Antes de usar un panel, cada persona acepta la versión vigente del aviso de privacidad y de sus
 * términos (arquitectura 2.12). Si no hay nada pendiente (o aún no hay documentos publicados), no
 * se muestra nada. También pone el aviso de cookies del panel.
 */
export function ConsentimientoPendiente({ children }: { children: ReactNode }) {
  const api = useApi();
  const { salir } = useSesion();
  const [pendientes, setPendientes] = useState<Pendiente[] | null>(null);
  const [textos, setTextos] = useState<Record<string, string>>({});
  const [acepto, setAcepto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api<Pendiente[]>('GET', '/platform/legal/pendientes')
      .then(async (p) => {
        if (!vivo) return;
        setPendientes(p);
        const contenidos = await Promise.all(
          p.map((d) =>
            api<{ contenido: string }>('GET', `/platform/legal/publico/MX/${d.tipo}`)
              .then((x) => [d.id, x.contenido] as const)
              .catch(() => [d.id, ''] as const),
          ),
        );
        if (vivo) setTextos(Object.fromEntries(contenidos));
      })
      // Si el API no responde, no se bloquea el panel: se volverá a pedir al entrar.
      .catch(() => vivo && setPendientes([]));
    return () => {
      vivo = false;
    };
  }, [api]);

  if (pendientes === null) return <div className="centro subtitulo">Cargando…</div>;
  if (!pendientes.length)
    return (
      <>
        {children}
        <AvisoCookies politica="/cookies" />
      </>
    );
  const aceptar = async () => {
    setError(null);
    try {
      setPendientes(
        await api<Pendiente[]>('POST', '/platform/legal/consentimientos', {
          documentos: pendientes.map((p) => p.id),
        }),
      );
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <div className="centro">
      <div className="entrada" style={{ maxWidth: 760, textAlign: 'left' }}>
        <h1>Antes de continuar</h1>
        <p className="subtitulo">
          Publicamos {pendientes.length === 1 ? 'una versión nueva' : 'versiones nuevas'} de lo
          siguiente. Léelas y acéptalas para seguir usando la plataforma.
        </p>
        {pendientes.map((p) => (
          <details key={p.id} open={pendientes.length === 1}>
            <summary>
              <strong>{p.titulo}</strong> · versión {p.version}
            </summary>
            <div style={{ maxHeight: 320, overflow: 'auto', padding: '0 8px' }}>
              <Markdown texto={textos[p.id] ?? 'Cargando…'} />
            </div>
          </details>
        ))}
        <label style={{ display: 'flex', gap: 8, margin: '16px 0' }}>
          <input type="checkbox" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} />
          <span>
            Leí y acepto {pendientes.length === 1 ? 'este documento' : 'estos documentos'}.
          </span>
        </label>
        {error && <p className="error">{error}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="boton primario" disabled={!acepto} onClick={() => void aceptar()}>
            Aceptar y continuar
          </button>
          <button className="boton" onClick={() => void salir()}>
            Salir
          </button>
        </div>
      </div>
    </div>
  );
}
